import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Clock, AlertTriangle, CheckCircle2, Pause, Hourglass } from 'lucide-react';

/**
 * Small status badge that renders the live SLA state of a ticket.
 *
 * Renders nothing for closed tickets (no SLA pressure left). For active
 * tickets, shows a coloured badge with remaining/breached time so the
 * admin or technician knows where to focus.
 *
 * Props:
 *   slaState: { status, remaining_seconds, due_at, hours }
 *   compact:  bool — if true, render a single icon + label
 */
const SLABadge = ({ slaState, compact = false }) => {
  if (!slaState) return null;
  if (slaState.status === 'closed') return null;

  const variant = ({
    on_track: { icon: Clock, label: 'On track', cls: 'border-primary/40 text-primary' },
    breaching_soon: { icon: AlertTriangle, label: 'Breaching soon', cls: 'border-amber-500/50 text-amber-400' },
    breached: { icon: AlertTriangle, label: 'Breached', cls: 'border-red-500/60 text-red-500 bg-red-500/10' },
    paused: { icon: Pause, label: 'Paused (waiting on client)', cls: 'border-blue-500/40 text-blue-400' },
    closed: { icon: CheckCircle2, label: 'Closed', cls: 'border-muted text-muted-foreground' },
  })[slaState.status] || { icon: Hourglass, label: 'SLA', cls: 'border-border text-muted-foreground' };

  const Icon = variant.icon;
  const remaining = formatSLARemaining(slaState.remaining_seconds, slaState.status);

  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-wide gap-1 ${variant.cls}`} data-testid="sla-badge">
      <Icon className="w-3 h-3" />
      {compact ? remaining : `${variant.label} · ${remaining}`}
    </Badge>
  );
};

const formatSLARemaining = (seconds, status) => {
  if (status === 'paused') return 'paused';
  if (status === 'closed') return '—';
  const abs = Math.abs(seconds || 0);
  const hours = Math.floor(abs / 3600);
  const mins = Math.floor((abs % 3600) / 60);
  if (seconds < 0) {
    if (hours > 0) return `over by ${hours}h ${mins}m`;
    return `over by ${mins}m`;
  }
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
};

export default SLABadge;
